// ---------------------------------------------------------------------------
// Prezio Onboard Widget — Config Sanitizer
// ---------------------------------------------------------------------------
// Runtime validation for widget configuration received from the host page.
// The widget is storage-agnostic — it never fetches config itself.
// The host page fetches config from GET /v1/onboard/widget-config/ and
// passes it to init(). This module sanitizes those values before use.
// ---------------------------------------------------------------------------

import type { ColorScheme } from '@/types/config';

// ---------------------------------------------------------------------------
// Valid values
// ---------------------------------------------------------------------------

/** Step groups the backend recognises. */
const VALID_STEP_GROUPS = new Set(['retailer', 'dso', 'authorities']);

/** Acceptable color scheme values. */
const VALID_COLOR_SCHEMES = new Set<string>(['light', 'dark', 'auto', 'system']);

/** Rough hex-color check: #RGB, #RRGGBB, #RRGGBBAA. */
const HEX_COLOR_RE = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** ISO 3166-1 alpha-2 country code pattern. */
const COUNTRY_CODE_RE = /^[A-Z]{2}$/;

// ---------------------------------------------------------------------------
// Country preset
// ---------------------------------------------------------------------------

/** Per-country predefined step values. */
export interface CountryPreset {
  /** Global consumer type hint. `null` means no preset. */
  default_consumer_type: string | null;
}

// ---------------------------------------------------------------------------
// Remote config shape (matches GET /v1/onboard/widget-config/ response)
// ---------------------------------------------------------------------------

/**
 * Shape of the server-persisted widget configuration.
 *
 * The host page fetches this from the Widget Config API and passes it
 * to `init()` via the `remoteConfig` prop. All fields are optional —
 * missing fields fall back to safe defaults.
 */
export interface WidgetRemoteConfig {
  config_version?: number;
  step_group_order?: string[];
  hidden_step_groups?: string[];
  demo_mode?: boolean;
  custom_logo_url?: string | null;
  border_radius?: number | null;
  color_scheme?: string | null;
  colors?: Record<string, string>;
  country_presets?: Record<string, CountryPreset>;
  updated_at?: string | null;
}

// ---------------------------------------------------------------------------
// Sanitised output
// ---------------------------------------------------------------------------

/** Validated, widget-safe version of the remote config. */
export interface SanitisedWidgetConfig {
  stepGroupOrder: string[];
  hiddenStepGroups: string[];
  demoMode: boolean;
  logoUrl: string | null;
  borderRadius: string | null;
  colorScheme: ColorScheme | null;
  colors: Record<string, string>;
  countryPresets: Record<string, CountryPreset>;
}

// ---------------------------------------------------------------------------
// Sanitisation helpers
// ---------------------------------------------------------------------------

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/** Filter an array to only valid step group names. */
function sanitizeStepGroups(groups: unknown): string[] {
  if (!Array.isArray(groups)) return [];
  return groups.filter((g): g is string => typeof g === 'string' && VALID_STEP_GROUPS.has(g));
}

/** Map backend `color_scheme` to our `ColorScheme` union, or `null`. */
export function sanitizeColorScheme(scheme: unknown): ColorScheme | null {
  if (typeof scheme !== 'string' || !VALID_COLOR_SCHEMES.has(scheme)) return null;
  // Backend uses 'auto', widget uses 'system' — normalise.
  if (scheme === 'auto') return 'system';
  return scheme as ColorScheme;
}

/** Validate hex colors and drop invalid entries. */
function sanitizeColors(colors: unknown): Record<string, string> {
  if (!colors || typeof colors !== 'object' || Array.isArray(colors)) return {};
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(colors)) {
    if (isNonEmptyString(val) && HEX_COLOR_RE.test(val)) {
      out[key] = val;
    }
  }
  return out;
}

/** Validate border radius: integer 0–50, convert to CSS px value. */
function sanitizeBorderRadius(radius: unknown): string | null {
  if (radius === null || radius === undefined) return null;
  if (typeof radius !== 'number' || !Number.isInteger(radius)) return null;
  if (radius < 0 || radius > 50) return null;
  return `${radius}px`;
}

/** Validate a URL — must be https. */
function sanitizeHttpsUrl(url: unknown): string | null {
  if (!isNonEmptyString(url)) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/** Validate country presets — drop entries with invalid country codes. */
function sanitizeCountryPresets(presets: unknown): Record<string, CountryPreset> {
  if (!presets || typeof presets !== 'object' || Array.isArray(presets)) return {};
  const out: Record<string, CountryPreset> = {};
  for (const [code, preset] of Object.entries(presets)) {
    const normalised = code.toUpperCase();
    if (!COUNTRY_CODE_RE.test(normalised)) continue;
    if (!preset || typeof preset !== 'object' || Array.isArray(preset)) continue;

    const typed = preset as Record<string, unknown>;
    const consumerType = typed['default_consumer_type'];

    // Accept string or null. Ignore invalid values.
    if (consumerType === null || isNonEmptyString(consumerType)) {
      out[normalised] = { default_consumer_type: consumerType };
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Main sanitizer
// ---------------------------------------------------------------------------

/**
 * Validate and normalise a remote widget config into safe widget props.
 *
 * - Unknown step group names are silently dropped.
 * - Invalid color values are dropped.
 * - Color scheme `'auto'` is mapped to `'system'`.
 * - Invalid border radius values are ignored.
 * - Logo URLs that are not HTTPS are ignored.
 * - Country presets with malformed keys are dropped.
 * - If `config` is `null`, `undefined`, or not an object, returns defaults.
 */
export function sanitizeWidgetConfig(config: unknown): SanitisedWidgetConfig {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return {
      stepGroupOrder: [],
      hiddenStepGroups: [],
      demoMode: false,
      logoUrl: null,
      borderRadius: null,
      colorScheme: null,
      colors: {},
      countryPresets: {},
    };
  }

  const c = config as WidgetRemoteConfig;

  return {
    stepGroupOrder: sanitizeStepGroups(c.step_group_order),
    hiddenStepGroups: sanitizeStepGroups(c.hidden_step_groups),
    demoMode: typeof c.demo_mode === 'boolean' ? c.demo_mode : false,
    logoUrl: sanitizeHttpsUrl(c.custom_logo_url),
    borderRadius: sanitizeBorderRadius(c.border_radius),
    colorScheme: sanitizeColorScheme(c.color_scheme),
    colors: sanitizeColors(c.colors),
    countryPresets: sanitizeCountryPresets(c.country_presets),
  };
}
